import classNames from "classnames";
import { Form, Formik, FormikErrors, FormikProps } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { object, string } from "yup";

import style from "./add-license-delivery.scss";
import Warning from "components/icons/Warning";
import { OPPORTUNITY_ID_VALIDATION_FORMAT_1, OPPORTUNITY_ID_VALIDATION_FORMAT_2 } from "components/licenses/common";
import DeliveryFormContent, {
    createDefaultSelectedLicense,
    DeliveryForm,
    filterDefaultLicenses,
    isConfirmationDialogVisible,
    License,
    MAX_CASE_NUMBER_LENGTH,
    OPPORTUNITY_ID_LENGTH,
} from "components/licenses/delivery-history/DeliveryFormContent";
import LicenseSelectionForm from "components/licenses/delivery-history/LicenseSelectionForm";
import { isAnyLicenseInvalid } from "components/tenants/DeliveryTab";
import { DeliveryMode, LicenseToDeliver } from "domain/licenses";
import { CombinedTier, CONTRACT_TYPE_TRIAL, ContractType, LicensingModel, TenantType } from "domain/tenants";
import { extractTenantLicenseRateUuid, ProductToRateList } from "services/licenses/LicenseService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { isUserParentInternal } from "services/tenants/tenantCookieService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import { hasOnlyNumbers } from "utils/commonFunctions";
import {
    formatDateWithoutTime,
    formatFormDate,
    formatUtcDateString,
    HOUR,
    MINUTES_SECONDS_MILLISECONDS,
} from "utils/format";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const DEFAULT_SELECT_DELIVERY_TYPE = "delivery_type";

export function resolveDefaultTokenRateUuid(
    tenantRateUuid: string | undefined,
    rateVersions: ProductToRateList[],
    tenantType: TenantType
): string {
    return tenantRateUuid || extractTenantLicenseRateUuid(rateVersions, tenantType);
}

interface Props {
    submitEventHandler: (values: DeliveryForm, selectedLicenses: LicenseToDeliver[]) => Promise<void>;
    availableLicenses: License[];
    ownedLicenses: License[];
    preselectedFromBundle: boolean;
    licensingModel: LicensingModel;
    rateVersions: ProductToRateList[];
    tenantRateUuid?: string;
    tenantTier: CombinedTier;
    tenantType: TenantType;
    parentDrawbackPrevention: boolean;
    contractType: ContractType;
}

const connector = connect((state: StoreState) => ({
    theme: state.themeReducer.theme,
}));

const AddLicenseDeliveryForm = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [selectedLicenses, setSelectedLicenses] = React.useState<License[]>(
        props.ownedLicenses.length > 0 ? props.ownedLicenses : [createDefaultSelectedLicense(t)]
    );
    const [hiddenForm, setHiddenForm] = React.useState(false);
    const [formState, setFormState] = React.useState<DeliveryForm>();
    const [validateOnChange, setValidateOnChange] = React.useState(false);
    const [warningFlag, setWarningFlag] = React.useState<boolean>(false);
    const [licenseSelectionErrors, setLicenseSelectionErrors] = React.useState<boolean>(false);
    const DEFAULT_SELECTION_VALUE = "DEFAULT";

    const isAddButtonDisabled = (errors: FormikErrors<DeliveryForm>, dirty: boolean, submit: number) => {
        let hasFormErrors = Object.keys(errors).length > 0 || licenseSelectionErrors;
        if (selectedLicenses.length >= 1) {
            selectedLicenses.forEach((item) => {
                if (item.licenseType === "default" && submit > 0) {
                    hasFormErrors = true;
                }
            });
        }
        return (hasFormErrors && dirty) || hasFormErrors;
    };

    const validateForm = (values: DeliveryForm) => {
        const validationErrors: string[] = [];
        const isValueRequired = isUserParentInternal();

        if (!values.caseNumber && isValueRequired) {
            validationErrors.push(t("DeliveryHistory.addLicenseDelivery.validation.caseNumberRequired"));
        }

        if (!values.opportunityId && isValueRequired) {
            validationErrors.push(t("DeliveryHistory.addLicenseDelivery.validation.opportunityIdRequired"));
        }

        if (selectedLicenses.length < 1 || isAnyLicenseInvalid(selectedLicenses)) {
            validationErrors.push(t("DeliveryHistory.addLicenseDelivery.validation.licenseInvalid"));
        }

        selectedLicenses.forEach((item) => {
            if (item.licenseType === "default") {
                validationErrors.push(
                    t("DeliveryHistory.addLicenseDelivery.validation.licenseInvalid", { licenseType: item.licenseType })
                );
            }
        });
        return validationErrors;
    };

    const createConfirmationDialog = () => {
        const createdLicenses: License[] = [];
        const removedLicenses: License[] = [];
        selectedLicenses.forEach((each) => {
            if (each.licensesToAdd >= 0) {
                createdLicenses.push(each);
            } else {
                removedLicenses.push(each);
            }
        });
        return (
            <div>
                {removedLicenses.length > 0 && (
                    <div className={form.resultContainer}>
                        {warningFlag && (
                            <div className={classNames(form.warningBanner, form.fixedWidthModal)}>
                                <div className={style.textAndBannerContainer}>
                                    <Warning color={theme.secondaryWarningBackgroundColor} width={40} height={20} />
                                    {t("Common.withdrawalWarning")}
                                </div>
                            </div>
                        )}
                        <div>
                            {t("DeliveryHistory.addLicenseDelivery.confirmationDialog.removing")}
                            <ul>
                                {removedLicenses.map((each) => (
                                    <li key={each.licenseType}>
                                        {t("DeliveryHistory.addLicenseDelivery.license", {
                                            amount: each.licensesToAdd,
                                            productName: each.productName,
                                            expirationDate: formatDateWithoutTime(each.expirationDate),
                                        })}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
                {createdLicenses.length > 0 && (
                    <div className={form.resultContainer}>
                        {t("DeliveryHistory.addLicenseDelivery.confirmationDialog.adding")}
                        <ul>
                            {createdLicenses.map((each) => (
                                <li key={each.licenseType}>
                                    {t("DeliveryHistory.addLicenseDelivery.license", {
                                        amount: each.licensesToAdd,
                                        productName: each.productName,
                                        expirationDate: formatDateWithoutTime(each.expirationDate),
                                    })}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className={form.resultContainer}>
                    {t("DeliveryHistory.addLicenseDelivery.confirmationDialog.confirmation")}
                </div>
                <div className={buttons.buttonContainer}>
                    <button
                        type={"button"}
                        className={buttons.secondaryButtonWithoutIcon}
                        onClick={() => {
                            setHiddenForm(false);
                        }}
                        data-testid={testIds.common.dialog.closeButton}
                    >
                        {t("DeliveryHistory.addLicenseDelivery.confirmationDialog.keepEditing")}
                    </button>
                    <button
                        className={buttons.primaryButtonWithoutIcon}
                        data-testid={testIds.common.confirmationDialog.confirmButton}
                        onClick={async () => {
                            usageStatisticsService.sendEvent({
                                category: Category.LICENSE_DELIVERY,
                                action: Action.ADD_LICENSE_DELIVERY,
                            });

                            const licensesToDeliver = filterDefaultLicenses(selectedLicenses).map((each) => ({
                                licenseId: each.licenseType,
                                expirationDate: formatUtcDateString(
                                    formatFormDate(each.expirationDate),
                                    HOUR,
                                    MINUTES_SECONDS_MILLISECONDS,
                                    MINUTES_SECONDS_MILLISECONDS
                                ),
                                amount: each.licensesToAdd,
                                totalOfLicenses:
                                    each.deliveryMode == DeliveryMode.SET ? each.licensesToAdd : each.totalOfLicenses,
                                deliveryMode: each.deliveryMode || DeliveryMode.ADD,
                            }));
                            if (formState) {
                                const filteredLicenses = filterDefaultLicenses(selectedLicenses);
                                const allSameMode =
                                    filteredLicenses.length > 0 &&
                                    filteredLicenses.every(
                                        (license) =>
                                            (license.deliveryMode || DeliveryMode.ADD) ===
                                            (filteredLicenses[0].deliveryMode || DeliveryMode.ADD)
                                    );
                                const formDataWithAssignmentMode = allSameMode
                                    ? {
                                          ...formState,
                                          deliveryMode: filteredLicenses[0].deliveryMode,
                                      }
                                    : formState;

                                await props.submitEventHandler(formDataWithAssignmentMode, licensesToDeliver);
                            }
                        }}
                    >
                        {t("DeliveryHistory.addLicenseDelivery.confirmationDialog.confirm")}
                    </button>
                </div>
            </div>
        );
    };
    return (
        <Formik
            initialValues={{
                deliveryType: props.contractType === "TRIAL" ? CONTRACT_TYPE_TRIAL : "",
                caseNumber: "",
                opportunityId: "",
                notes: "",
                amount: null,
                expirationDate: "",
                bundle: DEFAULT_SELECTION_VALUE,
                tenantType: DEFAULT_SELECTION_VALUE,
                tokenRateVersion: resolveDefaultTokenRateUuid(
                    props.tenantRateUuid,
                    props.rateVersions,
                    props.tenantType
                ),
                targetDate: "",
                deliveryMode: DeliveryMode.ADD,
            }}
            onSubmit={async (values) => {
                const valuesToSubmit = { ...values };
                if (!valuesToSubmit.targetDate) {
                    delete valuesToSubmit.targetDate;
                } else {
                    valuesToSubmit.targetDate = new Date(valuesToSubmit.targetDate)
                        .toISOString()
                        .replace(/\.\d{3}Z$/, "Z");
                }
                const validationErrors = validateForm(values);
                if (validationErrors.length > 0) {
                    return;
                }
                const isConfirmationVisible = isConfirmationDialogVisible(
                    valuesToSubmit.deliveryType,
                    valuesToSubmit.opportunityId,
                    valuesToSubmit.caseNumber
                );
                if (isConfirmationVisible) {
                    setFormState(valuesToSubmit);
                    setHiddenForm(true);
                } else {
                    const licensesToDeliver = filterDefaultLicenses(selectedLicenses).map((each) => ({
                        licenseId: each.licenseType,
                        expirationDate: formatUtcDateString(
                            formatFormDate(each.expirationDate),
                            HOUR,
                            MINUTES_SECONDS_MILLISECONDS,
                            MINUTES_SECONDS_MILLISECONDS
                        ),
                        amount: each.licensesToAdd,
                        totalOfLicenses:
                            each.deliveryMode == DeliveryMode.SET ? each.licensesToAdd : each.totalOfLicenses,
                        deliveryMode: each.deliveryMode || DeliveryMode.ADD,
                    }));
                    const filteredLicenses = filterDefaultLicenses(selectedLicenses);
                    const allSameMode =
                        filteredLicenses.length > 0 &&
                        filteredLicenses.every(
                            (license) =>
                                (license.deliveryMode || DeliveryMode.ADD) ===
                                (filteredLicenses[0].deliveryMode || DeliveryMode.ADD)
                        );
                    // Send global assignment mode only if all licenses have the same mode
                    const formDataWithAssignmentMode = allSameMode
                        ? { ...valuesToSubmit, deliveryMode: filteredLicenses[0].deliveryMode }
                        : valuesToSubmit;

                    await props.submitEventHandler(formDataWithAssignmentMode, licensesToDeliver);
                }
            }}
            validationSchema={object().shape({
                deliveryType: string()
                    .required(t("DeliveryHistory.addLicenseDelivery.validation.deliveryType"))
                    .test(
                        "Default option selected",
                        t("DeliveryHistory.addLicenseDelivery.validation.deliveryType"),
                        (value) => value !== DEFAULT_SELECT_DELIVERY_TYPE
                    ),
                caseNumber: isUserParentInternal()
                    ? string()
                          .required(t("DeliveryHistory.addLicenseDelivery.validation.caseNumber"))
                          .typeError(t("DeliveryHistory.addLicenseDelivery.validation.caseNumberType"))
                          .max(
                              MAX_CASE_NUMBER_LENGTH,
                              t("DeliveryHistory.addLicenseDelivery.validation.caseNumberLength", {
                                  length: MAX_CASE_NUMBER_LENGTH,
                              })
                          )
                          .test(
                              "Has only numbers",
                              t("DeliveryHistory.addLicenseDelivery.validation.caseNumberType"),
                              (value: string | undefined) => hasOnlyNumbers(value)
                          )
                    : string()
                          .typeError(t("DeliveryHistory.addLicenseDelivery.validation.caseNumberType"))
                          .max(
                              MAX_CASE_NUMBER_LENGTH,
                              t("DeliveryHistory.addLicenseDelivery.validation.caseNumberLength", {
                                  length: MAX_CASE_NUMBER_LENGTH,
                              })
                          )
                          .test(
                              "Has only numbers",
                              t("DeliveryHistory.addLicenseDelivery.validation.caseNumberType"),
                              (value: string | undefined) => value === undefined || hasOnlyNumbers(value)
                          ),
                opportunityId: isUserParentInternal()
                    ? string()
                          .required(t("DeliveryHistory.addLicenseDelivery.validation.opportunityId"))
                          .matches(
                              /^(006|test)/i,
                              t("DeliveryHistory.addLicenseDelivery.validation.opportunityIdFormat", {
                                  option1: OPPORTUNITY_ID_VALIDATION_FORMAT_1,
                                  option2: OPPORTUNITY_ID_VALIDATION_FORMAT_2,
                              })
                          )
                          .test(
                              "Length validation",
                              t("DeliveryHistory.addLicenseDelivery.validation.opportunityIdLength", {
                                  length: OPPORTUNITY_ID_LENGTH,
                              }),
                              (value: string | undefined) => {
                                  if (!value) {
                                      return true;
                                  }
                                  return (
                                      value.toLowerCase().startsWith("test") || value.length === OPPORTUNITY_ID_LENGTH
                                  );
                              }
                          )
                    : string().length(
                          OPPORTUNITY_ID_LENGTH,
                          t("DeliveryHistory.addLicenseDelivery.validation.opportunityIdLength", {
                              length: OPPORTUNITY_ID_LENGTH,
                          })
                      ),
            })}
            validateOnChange={validateOnChange}
            validateOnBlur={false}
        >
            {({
                values,
                errors,
                handleChange,
                validateOnChange,
                touched,
                dirty,
                submitCount,
            }: FormikProps<DeliveryForm>) => {
                return (
                    <Form>
                        {!hiddenForm ? (
                            <>
                                <DeliveryFormContent
                                    availableLicenses={props.availableLicenses}
                                    ownedLicenses={props.ownedLicenses}
                                    selectedLicenses={selectedLicenses}
                                    setSelectedLicenses={(licenses) => setSelectedLicenses(licenses)}
                                    inTenantCreation={false}
                                    preselectedFromBundle={props.preselectedFromBundle}
                                    deliveryForm={values}
                                    errors={errors}
                                    handleChange={handleChange}
                                    licensingModel={props.licensingModel}
                                    rateVersions={props.rateVersions}
                                    selectedTier={props.tenantTier}
                                    validateOnChange={validateOnChange}
                                    touched={touched}
                                    tenantType={props.tenantType}
                                    contractType={props.contractType}
                                    inEdit={false}
                                />
                                <LicenseSelectionForm
                                    availableLicenses={props.availableLicenses}
                                    ownedLicenses={props.ownedLicenses}
                                    deliveryForm={values}
                                    selectedLicenses={selectedLicenses}
                                    setSelectedLicenses={(licenses) => setSelectedLicenses(licenses)}
                                    newDeal={false}
                                    parentDrawbackPrevention={props.parentDrawbackPrevention}
                                    setWarningFlag={setWarningFlag}
                                    setLicenseSelectionErrors={setLicenseSelectionErrors}
                                    selectedTier={props.tenantTier}
                                    deliveryMode={true}
                                />

                                <div className={style.gridRows}>
                                    <button
                                        type="submit"
                                        className={buttons.primaryButtonWithoutIcon}
                                        disabled={isAddButtonDisabled(errors, dirty, submitCount)}
                                        data-testid={
                                            testIds.workArea.license.deliveryHistory.createDeliveryDialog.addButton
                                        }
                                        onClick={() => {
                                            setValidateOnChange(true);
                                        }}
                                    >
                                        {t("DeliveryHistory.addLicenseDelivery.addButton")}
                                    </button>
                                </div>
                            </>
                        ) : (
                            createConfirmationDialog()
                        )}
                    </Form>
                );
            }}
        </Formik>
    );
};

export default connector(AddLicenseDeliveryForm);
